import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Concession } from '../../lib/api';
import { DialogState, ApprovalFormState } from './types';

interface ActionDialogsProps {
  concession: Concession;
  dialogs: DialogState;
  rejectionReason: string;
  approvalForm: ApprovalFormState;
  actionLoading: boolean;
  onDialogsChange: (dialogs: DialogState) => void;
  onRejectionReasonChange: (value: string) => void;
  onApprovalFormChange: (form: ApprovalFormState) => void;
  onApprove: () => void;
  onReject: () => void;
}

export function ActionDialogs({
  concession,
  dialogs,
  rejectionReason,
  approvalForm,
  actionLoading,
  onDialogsChange,
  onRejectionReasonChange,
  onApprovalFormChange,
  onApprove,
  onReject,
}: ActionDialogsProps) {
  return (
    <>
      {/* Reject Dialog */}
      <Dialog open={dialogs.reject} onOpenChange={(open) => onDialogsChange({ ...dialogs, reject: open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Concession</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this concession request.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => onRejectionReasonChange(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onDialogsChange({ ...dialogs, reject: false })}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onReject}
              disabled={actionLoading || !rejectionReason.trim()}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Reject Concession
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={dialogs.approve} onOpenChange={(open) => onDialogsChange({ ...dialogs, approve: open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Concession</DialogTitle>
            <DialogDescription>
              Provide approval details for this concession request.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="quantity_approved">Quantity Approved (optional)</Label>
              <Input
                id="quantity_approved"
                type="number"
                placeholder={`Default: ${concession.quantity_affected}`}
                value={approvalForm.quantityApproved}
                onChange={(e) => onApprovalFormChange({ ...approvalForm, quantityApproved: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="valid_until">Valid Until (optional)</Label>
              <Input
                id="valid_until"
                type="date"
                value={approvalForm.validUntil}
                onChange={(e) => onApprovalFormChange({ ...approvalForm, validUntil: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="approval_notes">Approval Notes (optional)</Label>
              <Textarea
                id="approval_notes"
                placeholder="Enter any approval notes..."
                value={approvalForm.notes}
                onChange={(e) => onApprovalFormChange({ ...approvalForm, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onDialogsChange({ ...dialogs, approve: false })}>
              Cancel
            </Button>
            <Button onClick={onApprove} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Approve Concession
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
